import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IWebhookEventDef } from '../../../../networked-shared-interfaces/constants/webhook-catalog';
import {
	IWebhookDeliveryDetailResponse, IWebhookDeliveryItem, WEBHOOK_DELIVERY_STATUSES, WebhookDeliveryStatus,
} from '../../../../networked-shared-interfaces/community-admin/external-vendor-webhooks';
import { toastService } from '../../../../services/toast.service';
import { WebhooksApi } from './useWebhooks';
import { deliveryStatusLabel, formatTime } from './webhookHelpers';

const DAY = 24 * 60 * 60 * 1000;

interface IProps {
	api: Pick<WebhooksApi, 'deliveries' | 'delivery' | 'resend'>;
	endpointId: string;
	events: readonly IWebhookEventDef[];
}

/** Last 30 days of deliveries for one endpoint. */
const WebhookDeliveryLog = ({ api, endpointId, events }: IProps) => {
	const [status, setStatus] = useState<WebhookDeliveryStatus | ''>('');
	const [event, setEvent] = useState('');
	const [rows, setRows] = useState<IWebhookDeliveryItem[]>([]);
	const [next, setNext] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [resending, setResending] = useState(false);
	const [viewId, setViewId] = useState<string | null>(null);
	const [detail, setDetail] = useState<IWebhookDeliveryDetailResponse | null>(null);
	const from = useMemo(() => Date.now() - 30 * DAY, []);

	const load = useCallback(async (cursor?: string) => {
		setLoading(true);
		try {
			const res = await api.deliveries({ id: endpointId, from, ...(status && { status }), ...(event && { event }), ...(cursor && { cursor }) });
			setRows((cur) => (cursor ? [...cur, ...res.items] : res.items));
			setNext(res.next);
		} catch (err: any) {
			toastService.error(err?.message || 'Could not load deliveries');
		} finally {
			setLoading(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [endpointId, from, status, event]);

	useEffect(() => { load(); }, [load]);

	useEffect(() => {
		setDetail(null);
		if (!viewId) return;
		api.delivery(viewId).then(setDetail).catch((err: any) => toastService.error(err?.message || 'Could not load delivery'));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [viewId]);

	const doResend = async (body: Parameters<WebhooksApi['resend']>[0]) => {
		setResending(true);
		const res = await api.resend(body);
		setResending(false);
		if (res) {
			toastService.success(`${res.queued} delivery(ies) queued`);
			load();
		}
	};

	return (
		<div className="form-group">
			<label><strong>Delivery log (last 30 days)</strong></label>
			<div className="form-inline mb-2">
				<select className="form-control mr-2" value={status} onChange={(e) => setStatus(e.target.value as WebhookDeliveryStatus | '')} aria-label="Status">
					<option value="">All statuses</option>
					{WEBHOOK_DELIVERY_STATUSES.map((s) => <option key={s} value={s}>{deliveryStatusLabel(s)}</option>)}
				</select>
				<select className="form-control mr-2" value={event} onChange={(e) => setEvent(e.target.value)} aria-label="Event">
					<option value="">All events</option>
					{events.map((e) => <option key={e.name} value={e.name}>{e.label}</option>)}
				</select>
				<button type="button" className="btn btn-outline-primary" disabled={resending} onClick={() => {
					if (!window.confirm('Resend every failed delivery from the last 30 days to this endpoint?')) return;
					doResend({ subscriptionId: endpointId, status: 'failed', from, to: Date.now() });
				}}>Resend all failed (30 days)</button>
			</div>

			<div className="table-responsive">
				<table className="table table-sm table-bordered">
					<thead><tr><th>Event</th><th>Time</th><th>Status</th><th>HTTP</th><th>Duration</th><th>Attempts</th><th /></tr></thead>
					<tbody>
						{rows.map((d) => (
							<React.Fragment key={d.id}>
								<tr>
									<td title={String(d.code)}>{d.event}{d.isTest && <small className="text-muted"> (test)</small>}</td>
									<td>{formatTime(d.createdAt)}</td>
									<td>{deliveryStatusLabel(d.status)}</td>
									<td>{d.httpStatus ?? '—'}</td>
									<td>{d.durationMs != null ? `${d.durationMs} ms` : '—'}</td>
									<td>{d.attempt}</td>
									<td className="text-nowrap">
										<button type="button" className="btn btn-link btn-sm" onClick={() => setViewId(viewId === d.id ? null : d.id)}>{viewId === d.id ? 'Hide body' : 'View body'}</button>
										<button type="button" className="btn btn-link btn-sm" disabled={resending} onClick={() => doResend({ id: d.id })}>Resend</button>
									</td>
								</tr>
								{viewId === d.id && (
									<tr>
										<td colSpan={7}>
											{!detail ? 'Loading...' : (
												<>
													{detail.error && <div className="alert alert-warning py-1">{detail.error}</div>}
													<pre className="mb-2" style={{ maxHeight: 300, overflow: 'auto' }}>{JSON.stringify(detail.body, null, 2)}</pre>
													{detail.responseSnippet && (<><strong>Response</strong><pre style={{ maxHeight: 200, overflow: 'auto' }}>{detail.responseSnippet}</pre></>)}
												</>
											)}
										</td>
									</tr>
								)}
							</React.Fragment>
						))}
					</tbody>
				</table>
			</div>
			{!loading && rows.length === 0 && <small className="text-muted">No deliveries yet.</small>}
			{next && <button type="button" className="btn btn-outline-secondary" disabled={loading} onClick={() => load(next)}>Load more</button>}
		</div>
	);
};

export default WebhookDeliveryLog;
